import HelpIcon from '@mui/icons-material/Help';
import { CircularProgress, Grid, Tooltip, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState, Activity } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AssignExecutor from 'components/shared/AssignExecutor';
import { default as StyledButton } from 'components/shared/Button';
import { sendCustomEmail } from 'queries/emailQueries';
import { ErrorAlert } from '../../components/atoms/Alert-message';
import Button from '../../components/atoms/button';
import FileUpload from '../../components/atoms/file-upload';
import InputTextAreaField from '../../components/atoms/input-field/inputTextAreaField';
import InputTextField from '../../components/atoms/input-field/inputTextField';
import Toast, {
  showToasterFailure,
  showToasterSuccess,
} from '../../components/atoms/toast';
import ReassignResource from '../../components/shared/ReassignResource';
import { APIHeader, APIHeaderForUpload } from '../../index';
import { errorNotification } from '../../reducers/handle-api-status/slice';
import { pecrURLData } from '../../reducers/pecr-url-data/slice';
import {
  DepartmentTitle,
  EffortCostGrid,
  EstimateLabel,
  LocalSiteButtons,
  LocalSiteComment,
  ReviewDoneMessage,
  SiteReviewTitle,
  SiteReviewValue,
  TextAsLink,
  UploadedDocument,
  boldFont,
  buttonCircularProgress,
  commentStyle,
  departmentFileUpload,
  homeButtonComponent,
  lscDescriptionValue,
  noFileUploaded,
  titleName,
  uploadedFilesGrid,
  validationErrorOnPages,
} from '../../styles';
import {
  exceptSymbolsInNumberTypeInput,
  getDownloadLink,
  getURL,
  isEmptyObject,
  preventPasteNegative,
  stripTrailingSlash,
  validateInputFields,
} from '../../utils';

//Estimation Department component
const Department = props => {
  const { apiConfig, userInfo, errorInAPI } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [departmentData, setDepartmentData] = useState({});
  const [displayFiles, setDisplayFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [disabled, setDisabled] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [departmentUpdateData, setDepartmentUpdateData] = useState({});
  const [inputValidationMessage, setInputValidationMessage] = useState('');
  const [docUploadedByCraetor, setDocUploadedByCraetor] = useState([]);
  const [docUploadedBySC, setDocUploadedBySC] = useState([]);
  const [docUploadedBySO, setDocUploadedBySO] = useState([]);
  const [actualEstimatedUserId, setActualEstimatedUserId] = useState(null);
  const [actualEstimatedUserEmail, setActualEstimatedUserEmail] =
    useState(null);
  const [showChangeExecutor, setShowChangeExecutor] = useState(false);

  const urlInfo = stripTrailingSlash(window.location.toString()).split('/');
  const eIndex = urlInfo.pop();
  const departmentId = urlInfo.pop();
  const siteId = urlInfo.pop();
  const pecrId = urlInfo.pop();
  const originURL = window.location.origin.toString();
  const url = getURL();

  const { mutate: sendCustomEmailMutation } = useMutation({
    mutationFn: sendCustomEmail,
    onError: error => {
      console.error('Error sending email:', error);
      dispatch(
        errorNotification({
          hasErrorOccured: true,
          message: t('error_message'),
        })
      );
    },
  });

  useEffect(() => {
    if (pecrId && siteId && departmentId) {
      dispatch(
        pecrURLData({
          pecrId: pecrId,
          siteId: siteId,
          departmentId: departmentId,
          eIndex: eIndex,
        })
      );
      //Call Estimation API to get the department data
      fetch(`${apiConfig.uiURL}api/Estimation`, {
        method: 'POST',
        headers: APIHeader(),
        body: JSON.stringify({
          pecrId: pecrId,
          siteId: siteId,
          departmentId: departmentId,
          Index: eIndex,
        }),
      })
        .then(async response => {
          const data = await response.json();
          setDepartmentData(data);
          setDepartmentUpdateData({
            comment: data.EstimatedComment,
            effort: data.EstimatedEffort,
            cost: data.EstimatedCost,
          });
          if (
            !isEmptyObject(data) &&
            (data.Status === 10 || data.Status === 11)
          ) {
            setDisabled(true);
          } else {
            setDisabled(false);
          }

          //Fetch earlier uploded files(uploaded on overview, site review and site owner pages) from Folder
          fetch(`${apiConfig.uiURL}api/FetchFilesFromSite`, {
            method: 'POST',
            headers: APIHeader(),
            body: JSON.stringify({
              MainFolder: 'PECROverview',
              SubFolder: pecrId,
              siteId: siteId,
            }),
          })
            .then(async response => {
              const files = await response.json();
              const filterFilesOnly = files.filter(file => {
                return file.includes('.');
              });
              setDepartmentData(prevState => {
                return {
                  ...prevState,
                  documents: filterFilesOnly,
                };
              });
            })
            .catch(() => {
              dispatch(
                errorNotification({
                  hasErrorOccured: true,
                  message: t('error_message'),
                })
              );
            });
        })
        .catch(() => {
          dispatch(
            errorNotification({
              hasErrorOccured: true,
              message: t('error_message'),
            })
          );
        });
      //Fetch Files from Folder based on department
      fetch(`${apiConfig.uiURL}api/FetchFilesFromDepartment`, {
        method: 'POST',
        headers: APIHeader(),
        body: JSON.stringify({
          MainFolder: 'PECROverview',
          SubFolder: pecrId,
          siteId: siteId,
          departmentId: departmentId,
          index: eIndex,
        }),
      })
        .then(async response => {
          const files = await response.json();
          setDisplayFiles(files);
        })
        .catch(() => {
          dispatch(
            errorNotification({
              hasErrorOccured: true,
              message: t('error_message'),
            })
          );
        });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pecrId, siteId, departmentId, eIndex, apiConfig]);

  useEffect(() => {
    if (pecrId && siteId) {
      //To fetch the files uploaded by Creator on Overview page
      fetch(`${apiConfig.uiURL}api/FetchFiles`, {
        method: 'POST',
        headers: APIHeader(),
        body: JSON.stringify({
          MainFolder: 'PECROverview',
          SubFolder: pecrId,
        }),
      })
        .then(async response => {
          const files = await response.json();
          const filterFilesOnly = files.filter(file => {
            return file.includes('.');
          });
          setDocUploadedByCraetor(filterFilesOnly);
        })
        .catch(() => {
          dispatch(
            errorNotification({
              hasErrorOccured: true,
              message: t('error_message'),
            })
          );
        });

      //Fetch Files from Folder uploaded by Site Coordinator
      fetch(`${apiConfig.uiURL}api/FetchFilesFromSiteReview`, {
        method: 'POST',
        headers: APIHeader(),
        body: JSON.stringify({
          MainFolder: 'PECROverview',
          SubFolder: pecrId,
          siteId: siteId,
          siteReview: 'SiteReview',
        }),
      })
        .then(async response => {
          const files = await response.json();
          const filterFilesOnly = files.filter(file => {
            return file.includes('.');
          });
          setDocUploadedBySC(filterFilesOnly);
        })
        .catch(() => {
          dispatch(
            errorNotification({
              hasErrorOccured: true,
              message: t('error_message'),
            })
          );
        });

      //Fetch Files from Folder uploaded by Site Owner
      fetch(`${apiConfig.uiURL}api/FetchFilesFromOwner`, {
        method: 'POST',
        headers: APIHeader(),
        body: JSON.stringify({
          MainFolder: 'PECROverview',
          SubFolder: pecrId,
          siteId: siteId,
          owner: 'Owner',
        }),
      })
        .then(async response => {
          const files = await response.json();
          const filterFilesOnly = files.filter(file => {
            return file.includes('.');
          });
          setDocUploadedBySO(filterFilesOnly);
        })
        .catch(() => {
          dispatch(
            errorNotification({
              hasErrorOccured: true,
              message: t('error_message'),
            })
          );
        });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pecrId, siteId, apiConfig]);

  //To handle the deletion of newly uploaded document
  const removeDoc = (event, fileName) => {
    event.preventDefault();
    const remainingFile = displayFiles.filter(file => {
      return file !== fileName;
    });
    setDisplayFiles(remainingFile);
    const remainingUploadFile = uploadedFiles.filter(file => {
      return file.name !== fileName;
    });
    setUploadedFiles(remainingUploadFile);
    fetch(`${apiConfig.uiURL}api/DeleteFile`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        MainFolder: 'PECROverview',
        SubFolder: pecrId,
        siteId: siteId,
        fileName: fileName,
        departmentId: departmentId,
        index: eIndex,
        PID: pecrId,
        folderName: 'Estimation',
        email: userInfo.userEmail,
      }),
    }).catch(() => {
      dispatch(
        errorNotification({
          hasErrorOccured: true,
          message: t('error_message'),
        })
      );
    });
    setErrorMessage('');
  };

  //To handle the onChange event of all updates
  const handleOnChange = (key, event) => {
    if (key === 'comment') {
      const eventValue = event.target.value.trim();
      setDepartmentUpdateData(prevState => {
        return {
          ...prevState,
          [key]: eventValue,
        };
      });
      const ValidationMessage = validateInputFields(eventValue, t);
      setInputValidationMessage(ValidationMessage);
    } else {
      setDepartmentUpdateData(prevState => {
        return {
          ...prevState,
          [key]: event.target.value,
        };
      });
    }
  };

  //Function to upload the files, calling on Save and Submit button clicks
  const fileUpload = (isSubmitClicked = false) => {
    const uploadData = new FormData();
    for (const file of uploadedFiles) {
      uploadData.append('file', file);
    }
    uploadData.append('MainFolder', 'PECROverview');
    uploadData.append('SubFolder', pecrId);
    uploadData.append('siteId', siteId);
    uploadData.append('departmentId', departmentId);
    uploadData.append('Index', eIndex);

    // API call to upload the document
    setShowProgress(true);
    fetch(`${apiConfig.uiURL}api/uploadDocForDepartment`, {
      headers: APIHeaderForUpload(),
      method: 'POST',
      body: uploadData,
    })
      .then(async res => {
        return (
          //stop showing progress bar once status is 200
          (
            res.status === 200 && setShowProgress(false),
            //navigate to list page
            isSubmitClicked && navigate('../List')
          )
        );
      })
      .catch(() => {
        setShowProgress(false);
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
  };

  const sendFinalMail = () => {
    // Send Email to Owner
    const ownerEmailPromise = fetch(`${apiConfig.uiURL}api/sendEmail`, {
      headers: APIHeader(),
      method: 'POST',
      body: JSON.stringify({
        PecrId: pecrId,
        toAddress: (departmentData && departmentData.OwnerEmail) || '',
        loginUserEmail: userInfo.userEmail,
        Title: (departmentData && departmentData.Title) || '',
        Description: (departmentData && departmentData.Description) || '',
        fromAddress: 'PECR@carrier.com',
        type: 'assignEmail',
        Factory: (departmentData && departmentData.SiteName) || '',
        toWhome: 'Site Owner Estimation',
        creatorsite: (departmentData && departmentData.CreatorSiteName) || '',
        creator: `${
          departmentData && departmentData.CreatorFirstName
            ? departmentData.CreatorFirstName
            : ''
        } ${
          departmentData && departmentData.CreatorLastName
            ? departmentData.CreatorLastName
            : ''
        }`,
      }),
    });

    // Send Email to LSC (Local Site Coordinator)
    const lscEmailPromise = fetch(`${apiConfig.uiURL}api/sendEmail`, {
      headers: APIHeader(),
      method: 'POST',
      body: JSON.stringify({
        PecrId: pecrId,
        toAddress: (departmentData && departmentData.LSCEmail) || '',
        loginUserEmail: userInfo.userEmail,
        Title: (departmentData && departmentData.Title) || '',
        Description: (departmentData && departmentData.Description) || '',
        fromAddress: 'PECR@carrier.com',
        type: 'assignEmail',
        URL: `${url}OwnerLSCReview/${pecrId}/${siteId}`,
        Factory: (departmentData && departmentData.SiteName) || '',
        toWhome: 'Site Coordinator Estimation',
        creatorsite: (departmentData && departmentData.CreatorSiteName) || '',
        creator: `${
          departmentData && departmentData.CreatorFirstName
            ? departmentData.CreatorFirstName
            : ''
        } ${departmentData && departmentData.CreatorLastName}`,
      }),
    });

    // Wait for both emails to complete
    Promise.all([ownerEmailPromise, lscEmailPromise]).catch(() => {
      dispatch(
        errorNotification({
          hasErrorOccured: true,
          message: t('error_message'),
        })
      );
    });
  };

  const onNotImpactedClick = () => {
    fetch(`${apiConfig.uiURL}api/UpdateEstimation`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        pecrId: pecrId,
        siteId: siteId,
        departmentId: departmentId,
        EIndex: eIndex,
        UserId: userInfo.userID,
        Comments: departmentUpdateData.comment,
        Label:
          departmentData && departmentData.Label ? departmentData.Label : null,
        NotImapcted: 1,
        Status: 11,
        Currency: departmentData.Currency,
      }),
    })
      .then(async res => {
        const response = await res.json();
        //To check the Status of all department before sending mail
        fetch(`${apiConfig.uiURL}api/FetchDepartmentStatus`, {
          method: 'POST',
          headers: APIHeader(),
          body: JSON.stringify({
            pecrId: pecrId,
            siteId: siteId,
          }),
        })
          .then(async response => {
            let shouldSendMail = false;
            const data = await response.json();
            const inProcessDepartments = data.filter(department => {
              return department.StatusName === 'Estimating in process';
            });
            if (inProcessDepartments.length === 0) {
              shouldSendMail = true;
            }
            shouldSendMail && sendFinalMail();
          })
          .catch(() => {
            dispatch(
              errorNotification({
                hasErrorOccured: true,
                message: t('error_message'),
              })
            );
          });
        fetch(`${apiConfig.uiURL}api/InsertEstimationForSC`, {
          method: 'POST',
          headers: APIHeader(),
          body: JSON.stringify({
            pecrId: pecrId,
            siteId: siteId,
            Status: response[0].FinalStaus,
            TotalEffort: response[0].TotalEffort,
            TotalCost: response[0].TotalCost,
            reviwerId: departmentData.CoordinatorID,
            GlobalCoordinatorID: departmentData.GlobalCoordinatorID,
          }),
        }).catch(() => {
          dispatch(
            errorNotification({
              hasErrorOccured: true,
              message: t('error_message'),
            })
          );
        });
        //navigate to list page
        navigate('../List');
      })
      .catch(() => {
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
  };

  const onSaveClick = () => {
    setShowProgress(true);
    const updateData = fetch(`${apiConfig.uiURL}api/UpdateEstimation`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        pecrId: pecrId,
        siteId: siteId,
        departmentId: departmentId,
        EIndex: eIndex,
        UserId: userInfo.userID,
        EstimateEffort: departmentUpdateData.effort,
        EstimateCost: departmentUpdateData.cost,
        Currency: departmentData.Currency,
        Comments: departmentUpdateData.comment,
        Label:
          departmentData && departmentData.Label ? departmentData.Label : null,
      }),
    })
      .then(async res => {
        const response = await res.json();
        fileUpload();
        fetch(`${apiConfig.uiURL}api/InsertEstimationForSC`, {
          method: 'POST',
          headers: APIHeader(),
          body: JSON.stringify({
            pecrId: pecrId,
            siteId: siteId,
            Status: response[0].FinalStaus,
            TotalEffort: response[0].TotalEffort,
            TotalCost: response[0].TotalCost,
            reviwerId: departmentData.CoordinatorID,
            GlobalCoordinatorID: departmentData.GlobalCoordinatorID,
          }),
        }).catch(() => {
          dispatch(
            errorNotification({
              hasErrorOccured: true,
              message: t('error_message'),
            })
          );
        });
      })
      .catch(() => {
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });

    Promise.all([updateData])
      .then(() => {
        showToasterSuccess(t('Save_message'));
      })
      .catch(() => {
        showToasterFailure(t('Save_failed_message'));
      });
  };

  const onSubmitClick = () => {
    fetch(`${apiConfig.uiURL}api/UpdateEstimation`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        pecrId: pecrId,
        siteId: siteId,
        departmentId: departmentId,
        EIndex: eIndex,
        UserId: userInfo.userID,
        ActualEstimatedUserId: actualEstimatedUserId || userInfo.userID,
        EstimateEffort: departmentUpdateData.effort,
        EstimateCost: departmentUpdateData.cost,
        Currency: departmentData.Currency,
        Comments: departmentUpdateData.comment,
        Label:
          departmentData && departmentData.Label ? departmentData.Label : null,
        Status: 10, //Estimated
      }),
    })
      .then(async res => {
        const response = await res.json();
        fileUpload(true);
        //To check the Status of all department before sending mail
        fetch(`${apiConfig.uiURL}api/FetchDepartmentStatus`, {
          method: 'POST',
          headers: APIHeader(),
          body: JSON.stringify({
            pecrId: pecrId,
            siteId: siteId,
          }),
        })
          .then(async response => {
            let shouldSendMail = false;
            const data = await response.json();
            const inProcessDepartments = data.filter(department => {
              return department.StatusName === 'Estimating in process';
            });
            if (actualEstimatedUserEmail) {
              // Send email to the new assigned executor
              sendCustomEmailMutation({
                toAddress: [actualEstimatedUserEmail],
                fromAddress: 'PECR@carrier.com',
                subject: `You have been assigned as Executor on PECR ${pecrId} With Title: ${departmentData.Title}`,
                body: `You have been assigned as the Executor for the department ${departmentData.DepartmentName} in PECR ${pecrId}. Please check the details in the Tracking File.`,
                link: `${originURL}/TrackingFile?id=${pecrId}`,
              });
            }
            if (inProcessDepartments.length === 0) {
              shouldSendMail = true;
            }
            shouldSendMail && sendFinalMail();
          })
          .catch(() => {
            dispatch(
              errorNotification({
                hasErrorOccured: true,
                message: t('error_message'),
              })
            );
          });
        fetch(`${apiConfig.uiURL}api/InsertEstimationForSC`, {
          method: 'POST',
          headers: APIHeader(),
          body: JSON.stringify({
            pecrId: pecrId,
            siteId: siteId,
            Status: response[0].FinalStaus,
            TotalEffort: response[0].TotalEffort,
            TotalCost: response[0].TotalCost,
            reviwerId: departmentData.CoordinatorID,
            GlobalCoordinatorID: departmentData.GlobalCoordinatorID,
          }),
        }).catch(() => {
          dispatch(
            errorNotification({
              hasErrorOccured: true,
              message: t('error_message'),
            })
          );
        });
      })
      .catch(() => {
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
  };

  //To navigate on EstimateReview page
  const onPECRLinkClick = () => {
    navigate('../EstimateReview');
  };

  //To navigate on Tracking page in new tab
  const onPECRSummaryLinkClick = () => {
    //navigating to tracking page based on pecrId
    window.open(`${originURL}/TrackingFile?id=${pecrId}`, '_blank');
  };

  //To download the file uploaded by Creator on Overview page
  const onDownloadFileFromCreatorClick = filename => {
    fetch(`${apiConfig.uiURL}api/DownloadFilesForLSC`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        MainFolder: 'PECROverview',
        SubFolder: pecrId,
        filename: filename,
      }),
    })
      .then(async response => {
        response.blob().then(blob => {
          getDownloadLink(blob, filename);
        });
      })
      .catch(() => {
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
  };

  //To download the file uploaded by Site Coordinator on Site Coordinator page
  const onDownloadFileForSiteCoordinatorClick = filename => {
    fetch(`${apiConfig.uiURL}api/DownloadFileForSiteReview`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        MainFolder: 'PECROverview',
        SubFolder: pecrId,
        siteId: siteId,
        filename: filename,
        siteReview: 'SiteReview',
      }),
    })
      .then(async response => {
        response.blob().then(blob => {
          getDownloadLink(blob, filename);
        });
      })
      .catch(() => {
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
  };

  //To download the file uploaded by Owner on Site Owner page
  const onDownloadFileForSiteOwnerClick = filename => {
    fetch(`${apiConfig.uiURL}api/DownloadFileForOwner`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        MainFolder: 'PECROverview',
        SubFolder: pecrId,
        siteId: siteId,
        filename: filename,
        owner: 'Owner',
      }),
    })
      .then(async response => {
        response.blob().then(blob => {
          getDownloadLink(blob, filename);
        });
      })
      .catch(() => {
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
  };

  //To download the files on DownloadFile API click
  const onDownloadFileClick = filename => {
    fetch(`${apiConfig.uiURL}api/DownloadFile`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        MainFolder: 'PECROverview',
        SubFolder: pecrId,
        siteId: siteId,
        departmentId: departmentId,
        index: eIndex,
        filename: filename,
      }),
    })
      .then(async response => {
        response.blob().then(blob => {
          getDownloadLink(blob, filename);
        });
      })
      .catch(() => {
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
  };

  if (errorInAPI.hasErrorOccured) {
    return <ErrorAlert message={errorInAPI.message} />;
  }
  if (isEmptyObject(departmentData) || (userInfo && userInfo.userId === '')) {
    return (
      <Grid style={homeButtonComponent}>
        <CircularProgress />
      </Grid>
    );
  }
  if (
    userInfo?.active?.toString() !== '1' ||
    userInfo?.userId?.toString() === '0' ||
    userInfo?.userId === null ||
    Number(userInfo?.userID) !== Number(departmentData.EstimatedUserID)
  ) {
    return (
      <Grid style={homeButtonComponent}>{t('No_active_user_message')}</Grid>
    );
  }

  return (
    <Grid>
      {disabled && (
        <Grid style={ReviewDoneMessage}>
          {t('Department_has_been_already_reviewed')}
        </Grid>
      )}
      <Grid style={DepartmentTitle}>
        <Grid>
          <h4 style={titleName}>{`${
            departmentData && departmentData.DepartmentName
              ? departmentData.DepartmentName
              : ''
          } ${t('Estimate')}`}</h4>
        </Grid>
        <Grid
          sx={{
            display: 'flex',
            gap: '25px',
          }}
        >
          <Grid style={TextAsLink} onClick={onPECRSummaryLinkClick}>
            {t('PECR_Summary_Page')}
          </Grid>
          <Grid style={TextAsLink} onClick={onPECRLinkClick}>
            {t('PECR_Overview')}
          </Grid>
        </Grid>
      </Grid>
      <Grid>
        <Grid style={SiteReviewTitle}>
          <Grid style={boldFont}>{t('Title')}:</Grid>
          <Grid style={lscDescriptionValue}>
            {`${
              departmentData && departmentData.Title ? departmentData.Title : ''
            }(${pecrId})`}
          </Grid>
        </Grid>
        <Grid style={SiteReviewTitle}>
          <Grid style={boldFont}>{t('Description')}:</Grid>
          <Grid style={lscDescriptionValue}>
            {departmentData && departmentData.Description
              ? departmentData.Description
              : ''}
          </Grid>
        </Grid>
        <Grid style={SiteReviewTitle}>
          <Grid style={boldFont}>{t('Product_Family')}:</Grid>
          <Grid style={SiteReviewValue}>
            {departmentData && departmentData.ProductFamily
              ? departmentData.ProductFamily.split(',').join(', ')
              : ''}
          </Grid>
        </Grid>
        <Grid style={SiteReviewTitle}>
          <Grid style={boldFont}>{t('Creator')}:</Grid>
          <Grid style={SiteReviewValue}>{`${
            departmentData && departmentData.CreatorFirstName
              ? departmentData.CreatorFirstName
              : ''
          } ${
            departmentData && departmentData.CreatorLastName
              ? departmentData.CreatorLastName
              : ''
          } (${
            departmentData && departmentData.CreatorSiteName
              ? departmentData.CreatorSiteName
              : ''
          })`}</Grid>
        </Grid>
        <Grid style={SiteReviewTitle}>
          <Grid style={boldFont}>{t('Owner')}:</Grid>
          <Grid style={SiteReviewValue}>{`${
            departmentData && departmentData.OwnerFirstName
              ? departmentData.OwnerFirstName
              : ''
          } ${
            departmentData && departmentData.OwnerLastName
              ? departmentData.OwnerLastName
              : ''
          }`}</Grid>
        </Grid>
        <Grid style={SiteReviewTitle}>
          <Grid style={boldFont}>{t('Site_Name')}:</Grid>
          <Grid style={SiteReviewValue}>
            {departmentData && departmentData.SiteName
              ? departmentData.SiteName
              : ''}
          </Grid>
        </Grid>
        {departmentData && (
          <ReassignResource
            title={departmentData.Title}
            departmentName={departmentData.DepartmentName}
            updatedBy={departmentData.EstimatedUserID}
            siteCoordinator={departmentData.CoordinatorID}
            pecrId={pecrId}
            siteId={siteId}
            departmentId={departmentId}
            subDepartment={departmentData.Label ?? ''}
          />
        )}
        <div className="mb-4 ml-20 w-fit">
          {!showChangeExecutor && (
            <StyledButton
              message="Change Executor"
              type="primary"
              size="medium"
              onClick={() => setShowChangeExecutor(true)}
            />
          )}
          <Activity mode={showChangeExecutor ? 'visible' : 'hidden'}>
            <AssignExecutor
              departmentId={departmentId}
              siteId={siteId}
              setActualEstimatedUserId={setActualEstimatedUserId}
              setShowChangeExecutor={setShowChangeExecutor}
              setActualEstimatedUserEmail={setActualEstimatedUserEmail}
            />
          </Activity>
        </div>
        {eIndex !== '0' && <Grid style={EstimateLabel} />}
        {eIndex !== '0' && (
          <Grid style={SiteReviewTitle}>
            <h4 style={titleName}>{`${
              departmentData && departmentData.Label ? departmentData.Label : ''
            } ${t('Estimate')}`}</h4>
            <Tooltip
              title={`${
                departmentData && departmentData.Label
                  ? departmentData.Label
                  : ''
              } ${t('Estimate')}`}
              placement="top-start"
              componentsProps={{
                tooltip: {
                  sx: {
                    fontSize: '14px',
                  },
                },
              }}
            >
              <HelpIcon
                sx={{
                  fontSize: 17,
                  position: 'relative',
                  top: '10px',
                  left: '10px',
                }}
              />
            </Tooltip>
          </Grid>
        )}
        <Grid style={EffortCostGrid}>
          <Grid style={EffortCostGrid}>
            <Grid style={{ marginRight: '5px' }}>{t('Effort')}:</Grid>
            <Grid width={110}>
              <InputTextField
                name={'Effort'}
                placeholder={t('Effort')}
                style={{
                  border: '1px solid black',
                  padding: '0px 5px',
                  borderRadius: '5px 0px 0px  5px ',
                }}
                value={departmentUpdateData.effort}
                onChange={event => handleOnChange('effort', event)}
                type={'number'}
                disabled={disabled}
                onKeyDown={e =>
                  exceptSymbolsInNumberTypeInput.includes(e.key) &&
                  e.preventDefault()
                }
                onPaste={preventPasteNegative}
                min="0"
              />
            </Grid>
            <Grid
              style={{
                border: '1px solid black',
                padding: '0px 7px',
                borderRadius: '0px 5px 5px  0px ',
              }}
            >
              {t('hrs')}
            </Grid>
          </Grid>
          <Grid style={EffortCostGrid}>
            <Grid style={{ marginRight: '5px' }}>{t('Additional_Cost')}:</Grid>
            <Grid width={110}>
              <InputTextField
                name={'AdditionalCost'}
                placeholder={t('Cost')}
                style={{
                  border: '1px solid black',
                  padding: '0px 5px',
                  borderRadius: '5px 0px 0px  5px ',
                }}
                value={departmentUpdateData.cost}
                onChange={event => handleOnChange('cost', event)}
                type={'number'}
                disabled={disabled}
                onKeyDown={e =>
                  exceptSymbolsInNumberTypeInput.includes(e.key) &&
                  e.preventDefault()
                }
                onPaste={preventPasteNegative}
                min="0"
              />
            </Grid>
            <Grid
              style={{
                border: '1px solid black',
                padding: '0px 7px',
                borderRadius: '0px 5px 5px  0px ',
              }}
            >
              {departmentData && departmentData.Currency
                ? departmentData.Currency
                : ''}
            </Grid>
          </Grid>
        </Grid>
        <Grid style={LocalSiteComment}>
          <InputTextAreaField
            name={'DepartmentComment'}
            placeholder={!disabled ? `${t('Add_Comments_here')}` : ''}
            onChange={event => handleOnChange('comment', event)}
            customStyle={commentStyle}
            value={departmentUpdateData.comment}
            disabled={disabled}
          />
        </Grid>
        <Grid style={validationErrorOnPages}>{inputValidationMessage}</Grid>
      </Grid>
      <Grid style={SiteReviewTitle}>
        <Grid style={uploadedFilesGrid}>
          <Grid style={UploadedDocument}>
            {`${t('Doc_uploaded_by_Creator')}:`}
          </Grid>
          <Grid style={UploadedDocument}>
            {docUploadedByCraetor.length > 0 ? (
              docUploadedByCraetor.map((document, index) => {
                return (
                  <Tooltip
                    key={index}
                    title={document}
                    componentsProps={{
                      tooltip: {
                        sx: {
                          fontSize: '14px',
                        },
                      },
                    }}
                  >
                    <Typography
                      key={index}
                      noWrap
                      width={400}
                      style={TextAsLink}
                      onClick={() => onDownloadFileFromCreatorClick(document)}
                    >
                      {document}
                    </Typography>
                  </Tooltip>
                );
              })
            ) : (
              <Grid style={noFileUploaded}>
                {t('No_files_have_been_uploaded')}
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>

      <Grid style={SiteReviewTitle}>
        <Grid style={uploadedFilesGrid}>
          <Grid style={UploadedDocument}>{`${t('Doc_uploaded_by_SC')}:`}</Grid>
          <Grid style={UploadedDocument}>
            {docUploadedBySC.length > 0 ? (
              docUploadedBySC.map((document, index) => {
                return (
                  <Tooltip
                    key={index}
                    title={document}
                    componentsProps={{
                      tooltip: {
                        sx: {
                          fontSize: '14px',
                        },
                      },
                    }}
                  >
                    <Typography
                      key={index}
                      noWrap
                      width={400}
                      style={TextAsLink}
                      onClick={() =>
                        onDownloadFileForSiteCoordinatorClick(document)
                      }
                    >
                      {document}
                    </Typography>
                  </Tooltip>
                );
              })
            ) : (
              <Grid style={noFileUploaded}>
                {t('No_files_have_been_uploaded')}
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>

      <Grid style={SiteReviewTitle}>
        <Grid style={uploadedFilesGrid}>
          <Grid style={UploadedDocument}>{`${t('Doc_uploaded_by_SO')}:`}</Grid>
          <Grid style={UploadedDocument}>
            {docUploadedBySO.length > 0 ? (
              docUploadedBySO.map((document, index) => {
                return (
                  <Tooltip
                    key={index}
                    title={document}
                    componentsProps={{
                      tooltip: {
                        sx: {
                          fontSize: '14px',
                        },
                      },
                    }}
                  >
                    <Typography
                      key={index}
                      noWrap
                      width={400}
                      style={TextAsLink}
                      onClick={() => onDownloadFileForSiteOwnerClick(document)}
                    >
                      {document}
                    </Typography>
                  </Tooltip>
                );
              })
            ) : (
              <Grid style={noFileUploaded}>
                {t('No_files_have_been_uploaded')}
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>

      {disabled ? (
        displayFiles && displayFiles.length > 0 ? (
          <Grid style={SiteReviewTitle}>
            <Grid style={uploadedFilesGrid}>
              <Grid style={UploadedDocument}>
                {t('Newly_Uploaded_Document')}:
              </Grid>
              <Grid style={UploadedDocument}>
                {displayFiles.map((document, index) => {
                  return (
                    <Tooltip
                      key={index}
                      title={document}
                      componentsProps={{
                        tooltip: {
                          sx: {
                            fontSize: '14px',
                          },
                        },
                      }}
                    >
                      <Typography
                        key={index}
                        noWrap
                        width={300}
                        style={TextAsLink}
                        onClick={() => onDownloadFileClick(document)}
                      >
                        {document}
                      </Typography>
                    </Tooltip>
                  );
                })}
              </Grid>
            </Grid>
          </Grid>
        ) : null
      ) : (
        <Grid style={departmentFileUpload}>
          <FileUpload
            displayFiles={displayFiles}
            setDisplayFiles={setDisplayFiles}
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
            removeDoc={removeDoc}
            disabled={disabled}
          />
        </Grid>
      )}
      <Toast />
      <Grid style={LocalSiteButtons}>
        <Button
          title={t('Not_Impacted')}
          onClick={onNotImpactedClick}
          disabled={disabled || inputValidationMessage !== ''}
        />
        <Grid sx={{ position: 'relative' }}>
          <Button
            title={t('Save')}
            onClick={onSaveClick}
            disabled={disabled || inputValidationMessage !== '' || showProgress}
          />
          {showProgress && (
            <CircularProgress size={28} sx={buttonCircularProgress} />
          )}
        </Grid>
        <Button
          title={t('Submit')}
          onClick={onSubmitClick}
          disabled={
            disabled ||
            departmentUpdateData.effort === '' ||
            departmentUpdateData.cost === '' ||
            inputValidationMessage !== ''
          }
        />
      </Grid>
    </Grid>
  );
};

const mapStateToProps = (state = {}) => {
  return {
    userInfo: state.userInfo,
    errorInAPI: state.errorNotification,
  };
};

export default connect(mapStateToProps, null)(Department);
